import { readAsText } from './promisedFileReader';

type FileReadFunction = (file: File) => Promise<string>;

const urlMatcher = /^URL=(.*)/;
const readKeyValFile: FileReadFunction = async (file) => {
    // For .url (windows) and .desktop (linux) files
    // Both have a format like

    /// [Header]
    /// Key=val
    /// Key2=val

    // We just need to find a line that matches
    // URL=https://whatever
    // as that's common to both formats and it's the only thing we care about.

    const contents: string = await readAsText(file);
    const lines = contents
        .split('\n')
        .map((x) => x.trim())
        .filter(Boolean);
    const urlLine = lines.find((l) => l.match(urlMatcher));
    if (!urlLine) {
        throw new Error('No valid link found in shortcut file');
    }
    return urlLine.slice(4);
};

const readPlistFile: FileReadFunction = async (file) => {
    // Mac shortcut files are a plist (that is, xml) file, with a URL entry in them somewhere
    // - parse the xml
    const contents = await readAsText(file);
    const parser = new DOMParser();
    const doc = parser.parseFromString(contents, 'text/xml');

    // - find the <key>URL</key>
    const keys = doc.getElementsByTagName('key');
    const urlKeyNode = [...keys].find((k) => k.innerHTML === 'URL');

    // - find the <string>https://something</string> that comes immediately after it
    const urlValueNode = urlKeyNode?.nextElementSibling;
    if (urlValueNode?.nodeName !== 'string') {
        throw new Error('No valid link found in shortcut file');
    }
    return urlValueNode.innerHTML;
};

const fileTypes: Record<string, FileReadFunction> = {
    url: readKeyValFile,
    desktop: readKeyValFile,
    webloc: readPlistFile,
};

export function isUrlFile(file: File): boolean {
    const [ext] = file.name.split('.').slice(-1);
    return ext in fileTypes;
}

export async function readUrlFromFile(file: File): Promise<string> {
    const [ext] = file.name.split('.').slice(-1);
    const reader = fileTypes[ext];
    if (!reader) {
        throw new Error('Not a supported link file');
    }
    return await reader(file);
}

export async function readLinkDataFromFile(file: File): Promise<{ url: string; error?: unknown }> {
    try {
        return {
            url: await readUrlFromFile(file),
        };
    } catch (error) {
        return {
            url: '',
            error,
        };
    }
}
